import { Block, Text } from '@snowflake/core-ui';
import { CortexLogo } from './CortexLogo';
import * as React from 'react';
import { Footer } from './Footer';
import { InternalHeaders } from '../utils/InternalHeaders';

export type ViewMode = 'viewA' | 'viewB';

export interface ToggleLabel {
  primary: string;
  secondary: string;
}

export interface ViewContent {
  label: ToggleLabel;
  leftContent: React.ReactNode;
  rightContent: React.ReactNode;
  aboveRightContent?: React.ReactNode;
  onActivate?: () => void;
}

interface UnifiedSignupContainerProps {
  viewA: ViewContent;
  viewB: ViewContent;
  initialView: ViewMode;
  recaptchaRef: any;
  pageNotFound?: boolean;
  onToggle?: (view: ViewMode) => void;
  singleView?: boolean;
}

export const UnifiedSignupContainer = (props: UnifiedSignupContainerProps) => {
  const { viewA, viewB, initialView, recaptchaRef, pageNotFound, onToggle, singleView } = props;

  // State for toggle
  const [selectedView, setSelectedView] = React.useState<ViewMode>(
    singleView ? 'viewA' : initialView,
  );

  // Get the current view content
  const currentView = singleView ? viewA : selectedView === 'viewA' ? viewA : viewB;

  // Handle toggle change
  const handleViewChange = (view: ViewMode) => {
    setSelectedView(view);
    const targetView = view === 'viewA' ? viewA : viewB;
    targetView.onActivate?.();
    onToggle?.(view);
  };

  const renderToggle = () => {
    if (singleView) {
      return (
        <Block display="flex" justifyContent="center" paddingTop={32}>
          <Block className="trial-toggle-container">
            <Block className="trial-toggle-button active single">
              <Text as="span" className="trial-toggle-label-primary">
                {viewA.label.primary}
              </Text>
              <Text as="span" className="trial-toggle-label-secondary">
                {viewA.label.secondary}
              </Text>
            </Block>
          </Block>
        </Block>
      );
    }

    const isViewA = selectedView === 'viewA';
    return (
      <Block display="flex" justifyContent="center" paddingTop={32}>
        <Block className="trial-toggle-container">
          <Block className={`trial-toggle-slider${isViewA ? '' : ' right'}`} />
          <Block
            className={`trial-toggle-button${isViewA ? ' active' : ''}`}
            onClick={() => handleViewChange('viewA')}
          >
            <Text as="span" className="trial-toggle-label-primary">
              {viewA.label.primary}
            </Text>
            <Text as="span" className="trial-toggle-label-secondary">
              {viewA.label.secondary}
            </Text>
          </Block>
          <Block
            className={`trial-toggle-button${!isViewA ? ' active' : ''}`}
            onClick={() => handleViewChange('viewB')}
          >
            <Text as="span" className="trial-toggle-label-primary">
              {viewB.label.primary}
            </Text>
            <Text as="span" className="trial-toggle-label-secondary">
              {viewB.label.secondary}
            </Text>
          </Block>
        </Block>
      </Block>
    );
  };

  const renderTitle = () => {
    return (
      <Block className="trial-page-title" display="flex" flexDirection="column" alignItems="center">
        <Text style={{ alignSelf: 'stretch', textAlign: 'center', margin: 0 }}>
          <Text as="span" className="trial-page-title-text" style={{ color: '#29B5E8' }}>
            BUILD FASTER WITH{' '}
          </Text>
          <Text as="span" className="trial-page-title-text" style={{ color: '#000' }}>
            SNOWFLAKE
          </Text>
        </Text>
        <Text className="trial-page-description">
          One platform for your data, apps and AI workflows
        </Text>
      </Block>
    );
  };

  return (
    <Block className="trial-page-container unified-style">
      <InternalHeaders />
      <CortexLogo />
      {renderTitle()}
      {renderToggle()}
      <Block className="trial-responsive-flex-container" position="relative" key={selectedView}>
        {currentView.leftContent}
        <Block
          className="trial-right-container"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          maxWidth="520px"
        >
          {currentView.aboveRightContent}
          {currentView.rightContent}
        </Block>
      </Block>
      <Footer recaptchaRef={recaptchaRef} pageNotFound={pageNotFound} />
    </Block>
  );
};
